// src/App.jsx
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import i18n from './i18n';

import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import SubscriptionRequiredModal from './components/SubscriptionRequiredModal';
import PublicRoute from './admin/components/PublicRoute';

// Route bundles (lazy — each loads only when the user navigates there)
import AdminRoutes from './routes/AdminRoutes';
import PortalRoutes from './routes/PortalRoutes';

// Auth / public pages (small, eager — shown before auth resolves)
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import OnboardUser from './pages/OnboardUser';
import Home from './pages/Home';

// Truly public (no auth, minimal weight)
const Apply = lazy(() => import('./pages/Apply'));
const ApplyStatus = lazy(() => import('./pages/ApplyStatus'));
const PublicOwnerContractSign = lazy(() => import('./pages/PublicOwnerContractSign'));

import './App.css';

const Spinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <AuthProvider>
            <CurrencyProvider>
              <Router>
                <Toaster position="top-right" />
                <SubscriptionRequiredModal />
                <div className="App">
                  <Suspense fallback={<Spinner />}>
                    <Routes>
                      {/* Fully public (no auth) */}
                      <Route path="/apply/:token" element={<Apply />} />
                      <Route path="/apply/status/:id" element={<ApplyStatus />} />
                      <Route path="/p/sign/owner/:contractId/:token" element={<PublicOwnerContractSign />} />
                      <Route path="/onboard/:token" element={<OnboardUser />} />

                      <Route path="/" element={<Home />} />
                      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

                      {/* Portal bundle */}
                      <Route path="/portal/*" element={<PortalRoutes />} />

                      {/* Admin bundle */}
                      <Route path="/admin/*" element={<AdminRoutes />} />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </div>
              </Router>
            </CurrencyProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;